import { useMemo, useState, useCallback } from "react"
import {
  Col,
  Row,
  Card,
  CardBody,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  Form,
  Input,
  Label,
  FormFeedback,
  UncontrolledTooltip,
} from "reactstrap"
import { useFormik } from "formik"
import * as Yup from "yup"
import { toast } from "react-toastify"

// Components
import Breadcrumbs from "components/Common/Breadcrumb"
import TableContainer from "components/Common/TableContainer"
import GlobalInputSearch from "components/Common/GlobalInputSearch"
import DeleteModal from "components/Common/DeleteModal"

// Hooks
import {
  useEngineCcs,
  useCreateEngineCc,
  useEditEngineCc,
  useDeleteEngineCc,
} from "hooks/engineCcHook"
import { useListParams } from "hooks/useListParams"

// Utils
import { formatReadableDate } from "utils/formatDate"
import { useToastMessage } from "hooks/useToastMessage"
import { useModels } from "hooks/modelHook"
import { modelToModelIdsNumber } from "utils/modelUtils"
import BadgeList from "components/Common/BadgeList"
import ButtonCheckboxList from "components/Common/ButtonCheckboxList"

const EngineCcList = () => {
  document.title = "Engine CC List"

  const [modal, setModal] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [engineCc, setEngineCc] = useState(null)
  const [deleteModal, setDeleteModal] = useState(false)
  const { handleResponse, handleError } = useToastMessage()

  const { params, setPage, setSearch, resetParams, hasActiveFilters } =
    useListParams({ page: 1, search: "" })

  const queryParams = useMemo(
    () => ({
      page: params.page,
      per_page: 10,
      with_models: true,
      ...(params.search && { search: params.search }),
    }),
    [params]
  )

  const {
    data: engineCcsResponse,
    isLoading,
    refetch: engineCcRefetch,
    isRefetching: engineCcRefetchLoading,
  } = useEngineCcs(queryParams)

  const { data: modelsResponse, isLoading: modelsFetchLoading } = useModels(
    { all: true },
    modal
  )

  const { mutateAsync: createEngineCc, isPending: engineCcCreateLoading } =
    useCreateEngineCc()
  const { mutateAsync: editEngineCc, isPending: engineCcEditLoading } =
    useEditEngineCc()
  const { mutateAsync: deleteEngineCc, isPending: engineCcDeleteLoading } =
    useDeleteEngineCc()

  const toggle = useCallback(() => {
    if (modal) {
      setModal(false)
      setEngineCc(null)
      setIsEdit(false)
    } else setModal(true)
  }, [modal])

  const handleAddNewClick = useCallback(() => {
    setEngineCc(null)
    setIsEdit(false)
    toggle()
  }, [toggle])

  const handleEditClick = useCallback(
    data => {
      setEngineCc(data)
      setIsEdit(true)
      toggle()
    },
    [toggle]
  )

  const onClickDelete = useCallback(data => {
    setEngineCc(data)
    setDeleteModal(true)
  }, [])

  const handleDelete = useCallback(async () => {
    if (!engineCc) return
    try {
      const res = await deleteEngineCc(engineCc.id)
      toast.success(res?.message ?? "Engine CC deleted successfully.")
      setDeleteModal(false)
    } catch (error) {
      toast.error("Failed to delete Engine CC.")
    }
  }, [engineCc, deleteEngineCc])

  const handleRefresh = () => engineCcRefetch()

  // Form validation
  const validationSchema = Yup.object({
    name: Yup.string().required("Please enter Engine CC"),
  })

  const onSubmit = useCallback(
    async (values, { resetForm }) => {
      try {
        const payload = {
          name: values.name,
          modelIds: values.selectedModels?.map(n => Number(n)),
        }
        const res = isEdit
          ? await editEngineCc({ id: engineCc.id, ...payload })
          : await createEngineCc(payload)

        handleResponse(
          res,
          `Engine CC ${isEdit ? "updated" : "created"} successfully.`
        )
        if (res.success) resetForm()
        toggle()
      } catch (error) {
        console.error(error)
        handleError(error, "Failed to save engine cc.")
      }
    },
    [isEdit, engineCc, editEngineCc, createEngineCc, toggle]
  )

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: engineCc?.name || "",
      selectedModels: modelToModelIdsNumber(engineCc?.models) || [],
    },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit,
  })

  // Table columns with action buttons like ModelList
  const columns = useMemo(
    () => [
      { header: "ID", accessorKey: "id" },
      { header: "Name", accessorKey: "name" },
      { header: "Slug", accessorKey: "slug" },
      {
        header: "Models",
        accessorKey: "models",
        cell: ({ row }) => <BadgeList items={row.original.models} />,
      },
      {
        header: "Created",
        accessorKey: "created_at",
        cell: ({ row }) => formatReadableDate(row.original.created_at),
      },
      {
        header: "Action",
        cell: ({ row }) => (
          <ul className="list-unstyled hstack gap-1 mb-0">
            <li>
              <button
                type="button"
                className="btn btn-sm btn-soft-info"
                onClick={() => handleEditClick(row.original)}
                id={`edittooltip-${row.original.id}`}
              >
                <i className="mdi mdi-pencil-outline" />
              </button>
              <UncontrolledTooltip
                placement="top"
                target={`edittooltip-${row.original.id}`}
              >
                Edit
              </UncontrolledTooltip>
            </li>
            <li>
              <button
                type="button"
                className="btn btn-sm btn-soft-danger"
                onClick={() => onClickDelete(row.original)}
                id={`deletetooltip-${row.original.id}`}
              >
                <i className="mdi mdi-delete-outline" />
              </button>
              <UncontrolledTooltip
                placement="top"
                target={`deletetooltip-${row.original.id}`}
              >
                Delete
              </UncontrolledTooltip>
            </li>
          </ul>
        ),
      },
    ],
    [handleEditClick, onClickDelete]
  )

  const engineCcsData = engineCcsResponse?.data?.data || []
  const paginationInfo = engineCcsResponse?.data

  return (
    <>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDelete}
        loading={engineCcDeleteLoading}
        onCloseClick={() => setDeleteModal(false)}
      />
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Engine CC" breadcrumbItem="Engine CC List" />
          <Row>
            <Col lg="12">
              <Card>
                <CardBody className="border-bottom">
                  <div className="d-flex align-items-center">
                    <h5 className="mb-0 flex-grow-1">Engine CC List</h5>
                    <div className="flex-shrink-0">
                      <Button
                        color="primary"
                        onClick={handleAddNewClick}
                        className="me-1"
                      >
                        Add New Engine CC
                      </Button>
                      <Button
                        color="light"
                        onClick={handleRefresh}
                        className="me-1"
                        id="refreshTooltip"
                      >
                        <i
                          className={`mdi mdi-refresh ${
                            engineCcRefetchLoading ? "mdi-spin" : ""
                          }`}
                        />
                        <UncontrolledTooltip
                          placement="top"
                          target="refreshTooltip"
                        >
                          Refresh
                        </UncontrolledTooltip>
                      </Button>

                      {hasActiveFilters && (
                        <Button
                          color="danger"
                          onClick={resetParams}
                          className="me-1"
                          id="resetTooltip"
                        >
                          <i className="mdi mdi-close" />
                          <UncontrolledTooltip
                            placement="top"
                            target="resetTooltip"
                          >
                            Reset Filters
                          </UncontrolledTooltip>
                        </Button>
                      )}
                    </div>
                  </div>
                </CardBody>
                <CardBody>
                  <Row className="mb-3">
                    <Col sm={4}>
                      <GlobalInputSearch
                        value={params.search}
                        onChange={setSearch}
                      />
                    </Col>
                  </Row>
                  <TableContainer
                    columns={columns}
                    data={engineCcsData}
                    isPagination={true}
                    paginationInfo={paginationInfo}
                    onPageChange={setPage}
                    loading={isLoading || engineCcRefetchLoading}
                    tableClass="align-middle table-nowrap dt-responsive nowrap w-100 table-check dataTable no-footer dtr-inline mt-4 border-top"
                    pagination="pagination"
                    paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Modal isOpen={modal} toggle={toggle} centered>
            <ModalHeader toggle={toggle}>
              {isEdit ? "Edit Engine CC" : "Add Engine CC"}
            </ModalHeader>
            <ModalBody>
              <Form
                onSubmit={e => {
                  e.preventDefault()
                  validation.handleSubmit()
                }}
              >
                <div className="mb-3">
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    placeholder="Enter Engine CC"
                    value={validation.values.name}
                    onChange={validation.handleChange}
                    invalid={!!validation.errors.name}
                  />
                  {validation.errors.name && (
                    <FormFeedback>{validation.errors.name}</FormFeedback>
                  )}
                </div>

                <div className="mb-3">
                  <Label>Select Models for This Engine CC</Label>
                  <div className="mb-3">
                    <ButtonCheckboxList
                      items={modelsResponse?.data}
                      name="selectedModels"
                      selected={validation.values.selectedModels}
                      onChange={newSelected =>
                        validation.setFieldValue("selectedModels", newSelected)
                      }
                      loading={modelsFetchLoading}
                    />
                  </div>
                </div>

                <div className="text-end">
                  <Button
                    type="submit"
                    color="success"
                    disabled={engineCcCreateLoading || engineCcEditLoading}
                  >
                    {isEdit ? "Save Changes" : "Add Engine CC"}
                    {(engineCcCreateLoading || engineCcEditLoading) && "..."}
                  </Button>
                </div>
              </Form>
            </ModalBody>
          </Modal>
        </div>
      </div>
    </>
  )
}

export default EngineCcList
